<script>
import Swal from "sweetalert2";

import { clienteComputed } from "@/state/helpers";
import { PRECIO_LISTA_MXN } from "@/services/suscripciones";
import { montarCampoDeTarjeta, mensajeDeErrorStripe, esModoPrueba } from "@/services/stripe";
import {
  buscarCliente,
  asegurarCliente,
  esBloqueoDelNavegador,
  crearIntentDeTarjeta,
  listarTarjetas,
  predeterminarTarjeta,
  eliminarTarjeta,
  suscripcionesDe,
  crearSuscripcion,
  cancelarSuscripcion,
  describirTarjeta,
  vencimientoDeTarjeta,
} from "@/services/membresia";

/**
 * Gestión de la membresía: tarjetas, alta y baja.
 *
 * Todas las operaciones terminan refrescando el estado en el módulo `cliente`,
 * porque de él dependen los muros de pago del resto del portal: sin ese
 * refresco, alguien que acaba de suscribirse seguiría viendo la invitación a
 * Plus hasta recargar la página.
 */
export default {
  data() {
    return {
      cliente: null,
      tarjetas: [],
      suscripcion: null,
      cargando: true,
      error: null,
      modoPrueba: esModoPrueba,
      precio: PRECIO_LISTA_MXN,
      /**
       * Las acciones de gestión viajan por POST y el backend no admite la
       * verificación previa CORS, así que hoy el navegador las bloquea. Se
       * detecta al listar tarjetas y se usa para no ofrecer botones que no
       * pueden funcionar. En cuanto se habilite CORS, esto se vuelve `true`
       * solo, sin tocar el código.
       */
      gestionDisponible: true,

      // Alta de tarjeta
      mostrandoFormulario: false,
      montandoTarjeta: false,
      guardandoTarjeta: false,
      stripe: null,
      campoTarjeta: null,

      // Operaciones en curso, para no permitir dos a la vez
      procesando: null,
    };
  },
  computed: {
    ...clienteComputed,
    tieneSuscripcion() {
      return !!this.suscripcion;
    },
    idTarjetaPredeterminada() {
      const s = this.suscripcion;
      return (s && s.default_payment_method) || (this.cliente && this.cliente.invoice_settings
        ? this.cliente.invoice_settings.default_payment_method
        : null);
    },
    importeMensual() {
      const monto = this.suscripcion && this.suscripcion.plan && this.suscripcion.plan.amount;
      const pesos = monto ? Math.round(monto / 100) : this.precio;
      return new Intl.NumberFormat("es-MX", {
        style: "currency",
        currency: "MXN",
        maximumFractionDigits: 0,
      }).format(pesos);
    },
  },
  methods: {
    describir: describirTarjeta,
    vencimiento: vencimientoDeTarjeta,

    esPredeterminada(metodo) {
      return metodo.id === this.idTarjetaPredeterminada;
    },

    /**
     * La suscripción se lee por GET y las tarjetas por POST. Como sólo el POST
     * puede estar bloqueado, se cargan por separado: perder las tarjetas no
     * debe impedir saber si la membresía está activa. Cuando iban juntas en un
     * `Promise.all`, un fallo del POST dejaba la pantalla afirmando que no
     * había suscripción justo encima de la etiqueta "Activo".
     */
    async cargar() {
      this.cargando = true;
      this.error = null;
      this.tarjetas = [];
      this.suscripcion = null;

      try {
        this.cliente = await buscarCliente(this.perfilCliente);
      } catch (err) {
        console.error("[membresia] error al buscar el cliente de Stripe:", err);
        this.error = "No pudimos consultar tu cuenta de pagos. Inténtalo de nuevo.";
        this.cargando = false;
        return;
      }

      // Sin cliente en Stripe todavía no hay nada que administrar: es el estado
      // normal de quien nunca ha registrado una tarjeta.
      if (!this.cliente) {
        this.cargando = false;
        return;
      }

      try {
        const suscripciones = await suscripcionesDe(this.cliente.id);
        this.suscripcion = suscripciones[0] || null;
      } catch (err) {
        console.error("[membresia] error al consultar la suscripción:", err);
      }

      try {
        this.tarjetas = await listarTarjetas(this.cliente.id);
        this.gestionDisponible = true;
      } catch (err) {
        console.error("[membresia] error al listar tarjetas:", err);
        this.gestionDisponible = !esBloqueoDelNavegador(err);
        if (this.gestionDisponible) {
          this.error = "No pudimos cargar tus métodos de pago. Inténtalo de nuevo.";
        }
      } finally {
        this.cargando = false;
      }
    },

    /** Refresca lo propio y el estado global del que dependen los muros. */
    async refrescar() {
      await this.cargar();
      await this.$store.dispatch("cliente/cargarMembresia");
    },

    async abrirFormulario() {
      this.mostrandoFormulario = true;
      this.montandoTarjeta = true;
      this.error = null;

      // El contenedor sólo existe cuando el formulario ya está en pantalla.
      await this.$nextTick();

      try {
        const { stripe, card } = await montarCampoDeTarjeta(this.$refs.campoTarjeta);
        this.stripe = stripe;
        this.campoTarjeta = card;
      } catch (err) {
        console.error("[membresia] error al montar el campo de tarjeta:", err);
        this.error = "No pudimos cargar el formulario de pago. Recarga la página e inténtalo de nuevo.";
        this.mostrandoFormulario = false;
      } finally {
        this.montandoTarjeta = false;
      }
    },

    cerrarFormulario() {
      if (this.campoTarjeta) {
        this.campoTarjeta.destroy();
        this.campoTarjeta = null;
      }
      this.mostrandoFormulario = false;
    },

    async guardarTarjeta() {
      if (!this.campoTarjeta || !this.stripe) return;

      this.guardandoTarjeta = true;
      this.error = null;

      try {
        const clientSecret = await crearIntentDeTarjeta(this.cliente.id);
        if (!clientSecret) throw new Error("El backend no devolvió el intent de pago.");

        // Los datos de la tarjeta van del iframe de Stripe directo a Stripe:
        // no pasan por este código ni por el backend.
        const resultado = await this.stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: this.campoTarjeta,
            billing_details: {
              name: (this.perfilCliente && this.perfilCliente.name) || "",
              email: this.perfilCliente && this.perfilCliente.email,
            },
          },
        });

        if (resultado.error) {
          this.error = mensajeDeErrorStripe(resultado.error);
          return;
        }

        const metodoId = resultado.setupIntent && resultado.setupIntent.payment_method;
        if (metodoId) await predeterminarTarjeta(this.cliente.id, metodoId);

        this.cerrarFormulario();
        await this.refrescar();
        Swal.fire("Tarjeta guardada", "Tu método de pago quedó registrado.", "success");
      } catch (err) {
        console.error("[membresia] error al guardar la tarjeta:", err);
        this.error = "No pudimos guardar la tarjeta. Inténtalo de nuevo.";
      } finally {
        this.guardandoTarjeta = false;
      }
    },

    async hacerPredeterminada(metodo) {
      this.procesando = metodo.id;
      try {
        await predeterminarTarjeta(this.cliente.id, metodo.id);
        await this.refrescar();
      } catch (err) {
        console.error("[membresia] error al cambiar la tarjeta principal:", err);
        Swal.fire("Error", "No pudimos cambiar la tarjeta principal.", "error");
      } finally {
        this.procesando = null;
      }
    },

    async quitarTarjeta(metodo) {
      // Quedarse sin tarjeta teniendo suscripción activa hace fallar el
      // siguiente cobro. Más vale decirlo ahora que dentro de un mes.
      const esLaUltima = this.tarjetas.length === 1;
      const advertencia =
        esLaUltima && this.tieneSuscripcion
          ? "Es tu única tarjeta y tu membresía sigue activa: el próximo cobro fallará y perderás el acceso."
          : "Podrás volver a registrarla cuando quieras.";

      const confirmacion = await Swal.fire({
        title: `¿Eliminar ${this.describir(metodo)}?`,
        text: advertencia,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      });
      if (!confirmacion.isConfirmed) return;

      this.procesando = metodo.id;
      try {
        await eliminarTarjeta(this.cliente.id, metodo.id);
        await this.refrescar();
        Swal.fire("Eliminada", "La tarjeta ya no está registrada.", "success");
      } catch (err) {
        console.error("[membresia] error al eliminar la tarjeta:", err);
        Swal.fire("Error", "No pudimos eliminar la tarjeta.", "error");
      } finally {
        this.procesando = null;
      }
    },

    async contratar() {
      if (this.tarjetas.length === 0) {
        this.error = "Registra una tarjeta antes de activar tu membresía.";
        return;
      }

      const confirmacion = await Swal.fire({
        title: "Activar Recnet Plus",
        html: `Se cobrará <strong>${this.importeMensual}</strong> a ${this.describir(this.tarjetas[0])}
               y se renovará cada mes hasta que la canceles.`,
        icon: "question",
        showCancelButton: true,
        confirmButtonText: "Sí, activar",
        cancelButtonText: "Cancelar",
      });
      if (!confirmacion.isConfirmed) return;

      this.procesando = "contratar";
      this.error = null;
      try {
        await crearSuscripcion(this.cliente.id);
        await this.refrescar();
        Swal.fire("¡Bienvenido a Plus!", "Tu membresía quedó activa.", "success");
      } catch (err) {
        console.error("[membresia] error al contratar:", err);
        this.error = "No pudimos activar tu membresía. Revisa tu tarjeta e inténtalo de nuevo.";
      } finally {
        this.procesando = null;
      }
    },

    async cancelar() {
      const confirmacion = await Swal.fire({
        title: "¿Cancelar tu membresía?",
        text: "Perderás el acceso a las sesiones y al directorio de negocios de inmediato, no al final del periodo pagado.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        confirmButtonText: "Sí, cancelar",
        cancelButtonText: "Conservar mi membresía",
      });
      if (!confirmacion.isConfirmed) return;

      this.procesando = "cancelar";
      try {
        await cancelarSuscripcion(this.suscripcion.id);
        await this.refrescar();
        Swal.fire("Membresía cancelada", "Puedes reactivarla cuando quieras.", "success");
      } catch (err) {
        console.error("[membresia] error al cancelar:", err);
        Swal.fire("Error", "No pudimos cancelar tu membresía. Inténtalo de nuevo.", "error");
      } finally {
        this.procesando = null;
      }
    },
  },
  mounted() {
    if (this.perfilCliente) this.cargar();
    else this.cargando = false;
  },
  beforeDestroy() {
    if (this.campoTarjeta) this.campoTarjeta.destroy();
  },
};
</script>

<template>
  <div class="gestion">
    <b-alert :show="modoPrueba" variant="warning" class="py-2 small">
      <strong>Modo de pruebas.</strong> Ningún cobro es real; usa las tarjetas de prueba de Stripe.
    </b-alert>

    <p v-if="cargando" class="text-muted mb-0">Cargando tu información de pago…</p>

    <template v-else>
      <b-alert :show="!!error" variant="danger" class="py-2 small">{{ error }}</b-alert>

      <!-- Cuando el navegador bloquea las peticiones de gestión, se dice qué
           pasa y dónde sí se puede hacer, en lugar de mostrar datos falsos. -->
      <b-alert :show="!gestionDisponible" variant="warning" class="py-2 small mb-0">
        <strong>La gestión de pagos no está disponible desde la web.</strong>
        El servidor de Recnet todavía no acepta estas operaciones desde el navegador.
        Mientras tanto, administra tu tarjeta y tu suscripción desde la app.
      </b-alert>

      <template v-if="gestionDisponible">
        <!-- Tarjetas -->
        <h3 class="h6 mt-3">Métodos de pago</h3>

        <p v-if="tarjetas.length === 0" class="text-muted small">
          No tienes ninguna tarjeta registrada.
        </p>

      <ul v-else class="list-unstyled mb-3">
        <li v-for="metodo in tarjetas" :key="metodo.id" class="tarjeta">
          <div>
            <span class="font-weight-bold">{{ describir(metodo) }}</span>
            <b-badge v-if="esPredeterminada(metodo)" variant="success" class="ml-2">Principal</b-badge>
            <div class="text-muted small">Vence {{ vencimiento(metodo) || '—' }}</div>
          </div>
          <div>
            <b-button v-if="!esPredeterminada(metodo)" size="sm" variant="outline-primary" class="mr-1"
              :disabled="!!procesando" @click="hacerPredeterminada(metodo)">
              Usar esta
            </b-button>
            <b-button size="sm" variant="outline-danger" :disabled="!!procesando"
              @click="quitarTarjeta(metodo)">
              <i class="mdi mdi-delete"></i>
            </b-button>
          </div>
        </li>
      </ul>

      <!-- Alta de tarjeta -->
      <div v-if="mostrandoFormulario" class="formulario-tarjeta">
        <label class="small font-weight-bold d-block mb-2">Datos de la tarjeta</label>
        <div ref="campoTarjeta" class="campo-stripe"></div>
        <p class="text-muted small mt-2 mb-3">
          Tus datos viajan directo a Stripe: ni Recnet ni este sitio los almacenan.
        </p>
        <div class="d-flex justify-content-end">
          <b-button size="sm" variant="secondary" class="mr-2" @click="cerrarFormulario">Cancelar</b-button>
          <b-button size="sm" variant="primary" :disabled="guardandoTarjeta || montandoTarjeta"
            @click="guardarTarjeta">
            {{ guardandoTarjeta ? 'Guardando…' : 'Guardar tarjeta' }}
          </b-button>
        </div>
      </div>

        <b-button v-else size="sm" variant="outline-primary" @click="abrirFormulario">
          <i class="mdi mdi-credit-card-plus-outline"></i>
          {{ tarjetas.length ? 'Agregar otra tarjeta' : 'Agregar tarjeta' }}
        </b-button>

        <!-- Alta y baja -->
        <hr />

        <div v-if="tieneSuscripcion">
          <p class="small text-muted mb-2">
            Tu membresía se renueva automáticamente por {{ importeMensual }} al mes.
          </p>
          <b-button size="sm" variant="outline-danger" :disabled="procesando === 'cancelar'"
            @click="cancelar">
            {{ procesando === 'cancelar' ? 'Cancelando…' : 'Cancelar membresía' }}
          </b-button>
        </div>

        <div v-else>
          <p class="small text-muted mb-2">
            Activa Recnet Plus por {{ importeMensual }} al mes y accede a las sesiones y al directorio.
          </p>
          <b-button size="sm" variant="success" :disabled="procesando === 'contratar'" @click="contratar">
            {{ procesando === 'contratar' ? 'Activando…' : 'Activar Recnet Plus' }}
          </b-button>
        </div>
      </template>
    </template>
  </div>
</template>

<style scoped>
.tarjeta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  border: 1px solid #e9ecef;
  border-radius: 8px;
  margin-bottom: 8px;
}

.formulario-tarjeta {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 16px;
  background: #f8f9fa;
}

.campo-stripe {
  background: #fff;
  border: 1px solid #ced4da;
  border-radius: 6px;
  padding: 12px;
}
</style>
